import { useState, type ReactElement } from 'react';
import { Link, useConfig, useNavigate, useSearchParams, useServices, useT } from '../../../app/di';
import { useResource } from '../../../shared/hooks';
import { Card } from '../../../ui-kit/Card/Card';
import { PreferencesControls } from '../../platform/user-preferences';
import { AuthLayout } from './AuthLayout/AuthLayout';
import { DemoAccountList } from './DemoAccountList/DemoAccountList';
import { LoginForm, type Credentials } from './LoginForm/LoginForm';

// IAM-UC-1, IAM-UC-1.CC-1, IAM-UC-1.CC-2, IAM-UC-3.CC-1, DEMO-UC-1 (демо-учётки)
export function LoginPage(): ReactElement {
  const { session, router, api } = useServices();
  const { t } = useT();
  const navigate = useNavigate();
  // IAM-UC-14.CC-1: ссылка на регистрацию — только пока функция включена.
  const registration = useConfig().features.registration;
  const search = useSearchParams();
  const info = useResource(() => api.operations.info(), [api]);
  const [preset, setPreset] = useState<Credentials | null>(null);

  const login = async ({ login, password }: Credentials): Promise<Awaited<ReturnType<typeof session.login>>> => {
    const result = await session.login(login, password);
    if (!result.success || !result.data) return result;
    // IAM-UC-3.CC-1: вернуться на исходную страницу.
    const next = search.get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      router.navigate(next, { replace: true });
      return result;
    }
    const lastBlogId = result.data.preferences.lastBlogId;
    const blogId = result.data.blogs.find((b) => b.id === lastBlogId)?.id ?? result.data.blogs[0]?.id;
    if (blogId) navigate('posts', { blogId }, { replace: true });
    else navigate('blogs', {}, { replace: true });
    return result;
  };

  return (
    <AuthLayout>
      <LoginForm onSubmit={login} preset={preset} />
      {registration ? (
        <p className="u-small">
          {t('identity.login.noAccount')} <Link to="register">{t('identity.login.toRegister')}</Link>
        </p>
      ) : null}
      {info.data?.demo && info.data.demoAccounts.length > 0 ? (
        <DemoAccountList accounts={info.data.demoAccounts} onPick={(account) => setPreset({ login: account.login, password: account.password })} />
      ) : null}
      <Card>
        <PreferencesControls compact />
      </Card>
    </AuthLayout>
  );
}
